import addressit from 'addressit';
import { parseLocation } from 'parse-address';
import {
  always,
  equals,
  filter,
  gt,
  has,
  head,
  ifElse,
  includes,
  isEmpty,
  join,
  length,
  map,
  match,
  nth,
  path,
  pathOr,
  replace,
  reverse,
  slice,
  split,
  toUpper,
  trim,
} from 'ramda';
import { isNotEmpty } from 'src/utilities/hasValue';

export const pruneToAddress = (data: any) => {
  const computeAddress = map(trim, reverse(split(',', data)));

  const remainAddress = gt(length(computeAddress), 4)
    ? join(', ', reverse(slice(3, length(computeAddress), computeAddress)))
    : nth(3, computeAddress) || '';

  const address = {
    country: nth(0, computeAddress) || '',
    state: nth(1, computeAddress) || '',
    city: nth(2, computeAddress) || '',
    street: remainAddress,
  };

  return {
    ...address,
    address1: path(['street'], address) === '' ? '' : path(['street'], address),
    address2: path(['street'], address) === '' ? '' : path(['street'], address),
    id: Math.floor(Math.random() * 100000),
  };
};

const _isNumeric = (n: any) => {
  return !isNaN(parseFloat(n)) && isFinite(n);
};

const _hasSome = (d: any) => {
  return !equals(d, 'USA') && !_isNumeric(d);
};

const hasState = (i: any) => {
  return ifElse(_hasSome, always(equals(length(i), 2)), always(false))(i);
};

const hasCity = (i: any) => {
  return ifElse(_hasSome, always(gt(length(i), 2)), always(false))(i);
};

const _findInRegion = (regions: any, hasFilter: any) =>
  head(filter(hasFilter, regions)) || '';

const _addressit = (data: any) => {
  const ad = addressit(data);
  const currentAddress = includes('USA', data) ? replace('USA', '', data) : data
  const zipCode = head(match(/\d{5}/g, currentAddress))
  if (!ad) return {};
  const regions = pathOr([], ['regions'], ad);
  return {
    street: pathOr('', ['street'], ad),
    city: _findInRegion(regions, hasCity),
    state: _findInRegion(regions, hasState),
    zip: _isNumeric(zipCode) ? zipCode : '',
    country: 'USA'
  };
};

const addressParser = (data: string) => {
  let parsed = parseLocation(data);
  const city = pathOr('', ['city'], parsed);
  const state = pathOr('', ['state'], parsed)
  if (!parsed || !city || !state) {
    return _addressit(data);
  }
  const STREET_FIELDS = ['number', 'prefix', 'street', 'type'];

  const suffixFn = (suff: string) => (key: string) => `${key}${suff}`;

  const streetFn = (suff: string) =>
    join(
      ' ',
      filter(isNotEmpty)(
        map(
          (k: string) => path([k], parsed),
          map(suffixFn(suff), STREET_FIELDS)
        )
      )
    ) || '';

  let street = streetFn('');

  if (has('street1', parsed)) {
    street = streetFn('1');
  }

  if (has('street2', parsed)) {
    street = join(', ', [street, streetFn('2')]);
  }

  if (isEmpty(street)) {
    street = pathOr('', ['0'], split(',', data))
  }
  return trimObjectValues({
    street,
    city: city,
    state: toUpper(state),
    zip: pathOr('', ['zip'], parsed),
    country: 'USA'
  });
};
//This method will trim the values of object
const trimObjectValues = (obj: Record<string, any>) =>
  Object.entries(obj).reduce((acc, [key, val]) => {
    acc[key] = typeof val === 'string' ? val.trim() : val;
    return acc;
  }, {} as Record<string, any>);


export const pruneAddress = (data: any) => {
  if (data) {
    const result = addressParser(data)
    return result;
  }
  return {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  };
};